import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, of, Subject, BehaviorSubject } from 'rxjs';
import { catchError, map, retryWhen, delay, take, concatMap, tap, finalize, switchMap } from 'rxjs/operators';
import { AppGlobals } from 'src/app/app.globals';

/**
 * Configurazione per i tentativi di retry e circuit breaker
 */
export interface AuthRetryConfig {
    /** Numero massimo di tentativi per ottenere il token OAuth */
    maxOAuthRetries: number;
    /** Numero massimo di tentativi per refresh token su 401 */
    maxRefreshRetries: number;
    /** Delay iniziale in ms per backoff esponenziale */
    initialDelayMs: number;
    /** Moltiplicatore per backoff esponenziale */
    backoffMultiplier: number;
    /** Delay massimo in ms */
    maxDelayMs: number;
    /** Timeout per circuit breaker in ms (tempo di attesa dopo apertura) */
    circuitBreakerTimeoutMs: number;
    /** Soglia di fallimenti per aprire il circuit breaker */
    circuitBreakerThreshold: number;
}

/**
 * Stato del Circuit Breaker
 */
export enum CircuitState {
    CLOSED = 'CLOSED',     // Normale funzionamento
    OPEN = 'OPEN',         // Bloccato, non permette richieste
    HALF_OPEN = 'HALF_OPEN' // Test per vedere se il servizio è tornato disponibile
}

/**
 * Interfaccia per i messaggi di errore UX
 */
export interface AuthErrorMessage {
    code: string;
    message: string;
    userFriendlyMessage: string;
    canRetry: boolean;
}

/**
 * Interfaccia per la risposta OAuth
 */
export interface OAuthTokenResponse {
    access_token: string;
    expires_in: number;
    token_type: string;
}

/**
 * Risposta wrapper dal microservizio Fea.Microservice
 */
export interface ApiResponse<T> {
    status: 'Success' | 'Error';
    message: string | null;
    data: T | null;
}

/**
 * Risposta token dal microservizio (contenuta in data)
 */
export interface MicroserviceTokenResponse {
    access_token: string;
    expires_in: number;
    token_type: string;
    refresh_token?: string;
    scope?: string;
}

/**
 * Servizio centralizzato per la gestione dell'autenticazione OAuth
 * con retry, backoff esponenziale, circuit breaker e sincronizzazione
 */
@Injectable({
    providedIn: 'root'
})
export class AuthTokenService {

    // Configurazione di default (usata come fallback se non presente nel file di config)
    private readonly defaultConfig: AuthRetryConfig = {
        maxOAuthRetries: 3,
        maxRefreshRetries: 3,
        initialDelayMs: 1000,
        backoffMultiplier: 2,
        maxDelayMs: 10000,
        circuitBreakerTimeoutMs: 30000,
        circuitBreakerThreshold: 5
    };

    // Configurazione effettiva (caricata da file o default)
    private config: AuthRetryConfig;

    // Storage centralizzato del token
    private _accessToken: string | null = null;
    private _tokenExpiry: Date | null = null;

    // Sincronizzazione per evitare race conditions
    private _isRefreshing = false;
    private _refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

    // Circuit Breaker
    private _circuitState: CircuitState = CircuitState.CLOSED;
    private _failureCount = 0;
    private _lastFailureTime: Date | null = null;
    private _circuitOpenedAt: Date | null = null;

    // Configurazioni servizi
    private _configurations: any = {};
    private _services: any;

    // Subject per notificare errori all'UI
    private _authError$ = new Subject<AuthErrorMessage>();
    public authError$ = this._authError$.asObservable();

    constructor(
        private http: HttpClient
    ) {
        this._configurations = AppGlobals.getAppConfigurations();
        this._services = this._configurations.services;

        // Carica configurazione da file, con fallback ai valori di default
        this.config = this.loadConfig();

        console.log('AuthTokenService initialized with config:', this.config);
    }

    /**
     * Carica la configurazione dal file di configurazione dell'applicazione.
     * Se la configurazione non è presente o è incompleta, usa i valori di default.
     */
    private loadConfig(): AuthRetryConfig {
        const fileConfig = this._services?.authRetry;

        if (!fileConfig) {
            console.warn('AuthRetryConfig not found in configuration file, using defaults');
            return { ...this.defaultConfig };
        }

        // Merge della configurazione da file con i default per eventuali parametri mancanti
        return {
            maxOAuthRetries: this.validatePositiveNumber(fileConfig.maxOAuthRetries, this.defaultConfig.maxOAuthRetries, 'maxOAuthRetries'),
            maxRefreshRetries: this.validatePositiveNumber(fileConfig.maxRefreshRetries, this.defaultConfig.maxRefreshRetries, 'maxRefreshRetries'),
            initialDelayMs: this.validatePositiveNumber(fileConfig.initialDelayMs, this.defaultConfig.initialDelayMs, 'initialDelayMs'),
            backoffMultiplier: this.validatePositiveNumber(fileConfig.backoffMultiplier, this.defaultConfig.backoffMultiplier, 'backoffMultiplier'),
            maxDelayMs: this.validatePositiveNumber(fileConfig.maxDelayMs, this.defaultConfig.maxDelayMs, 'maxDelayMs'),
            circuitBreakerTimeoutMs: this.validatePositiveNumber(fileConfig.circuitBreakerTimeoutMs, this.defaultConfig.circuitBreakerTimeoutMs, 'circuitBreakerTimeoutMs'),
            circuitBreakerThreshold: this.validatePositiveNumber(fileConfig.circuitBreakerThreshold, this.defaultConfig.circuitBreakerThreshold, 'circuitBreakerThreshold')
        };
    }

    /**
     * Valida che un valore sia un numero positivo, altrimenti usa il default
     */
    private validatePositiveNumber(value: any, defaultValue: number, paramName: string): number {
        if (typeof value === 'number' && value > 0) {
            return value;
        }
        if (value !== undefined) {
            console.warn(`Invalid value for ${paramName}: ${value}, using default: ${defaultValue}`);
        }
        return defaultValue;
    }

    /**
     * Restituisce la configurazione corrente (utile per debug/monitoring)
     */
    public getConfig(): AuthRetryConfig {
        return { ...this.config };
    }

    /**
     * Ottiene il token corrente se valido, altrimenti ne richiede uno nuovo
     */
    public getValidToken(): Observable<string> {
        // Se il token è valido, restituiscilo
        if (this.isTokenValid()) {
            return of(this._accessToken!);
        }

        // Altrimenti richiedi un nuovo token
        return this.acquireToken();
    }

    /**
     * Acquisisce un nuovo token OAuth con retry e backoff esponenziale
     */
    public acquireToken(): Observable<string> {
        // Controlla il circuit breaker
        if (!this.canMakeRequest()) {
            const error = this.createAuthError(
                'CIRCUIT_OPEN',
                'Circuit breaker is open',
                'Il servizio di autenticazione è temporaneamente non disponibile. Riprova tra qualche minuto.',
                false
            );
            this._authError$.next(error);
            return throwError(error);
        }

        // Se già in corso un refresh, attendi il risultato
        if (this._isRefreshing) {
            return this.waitForTokenRefresh();
        }

        this._isRefreshing = true;
        this._refreshTokenSubject.next(null);

        return this.requestOAuthToken().pipe(
            retryWhen(errors => this.handleRetryWithBackoff(errors, this.config.maxOAuthRetries)),
            tap(response => {
                // Memorizza token con scadenza dalla risposta OAuth
                this.storeToken(response.access_token, response.expires_in);
                this.resetCircuitBreaker();
                this._isRefreshing = false;
                this._refreshTokenSubject.next(response.access_token);
            }),
            map(response => response.access_token), // Restituisce solo il token come stringa
            catchError(error => {
                this._isRefreshing = false;
                this.recordFailure();

                const authError = this.createAuthError(
                    'TOKEN_ACQUISITION_FAILED',
                    error.message || 'Failed to acquire token',
                    'Impossibile ottenere l\'autorizzazione. Verifica la connessione e riprova.',
                    this._circuitState !== CircuitState.OPEN
                );
                this._authError$.next(authError);

                return throwError(authError);
            }),
            finalize(() => {
                this._isRefreshing = false;
            })
        );
    }

    /**
     * Gestisce la risposta HTTP 401 con refresh automatico del token
     */
    public handleUnauthorized<T>(
        retryRequest: (token: string) => Observable<T>,
        currentAttempt: number = 0
    ): Observable<T> {
        // Controlla se abbiamo superato il numero massimo di tentativi
        if (currentAttempt >= this.config.maxRefreshRetries) {
            const error = this.createAuthError(
                'MAX_REFRESH_RETRIES_EXCEEDED',
                `Max refresh retries (${this.config.maxRefreshRetries}) exceeded`,
                'Sessione scaduta. Effettua nuovamente l\'accesso.',
                false
            );
            this._authError$.next(error);
            return throwError(error);
        }

        // Controlla il circuit breaker
        if (!this.canMakeRequest()) {
            const error = this.createAuthError(
                'CIRCUIT_OPEN',
                'Circuit breaker is open',
                'Il servizio di autenticazione è temporaneamente non disponibile. Riprova tra qualche minuto.',
                false
            );
            this._authError$.next(error);
            return throwError(error);
        }

        // Se già in corso un refresh, attendi il risultato
        if (this._isRefreshing) {
            return this.waitForTokenRefresh().pipe(
                switchMap(token => {
                    if (token) {
                        return retryRequest(token);
                    }
                    return throwError(new Error('Token refresh failed'));
                })
            );
        }

        // Invalida il token corrente e richiedi uno nuovo
        this.clearToken();

        return this.acquireToken().pipe(
            switchMap(newToken => retryRequest(newToken)),
            catchError(error => {
                // Se fallisce ancora con 401, riprova ricorsivamente
                if (error instanceof HttpErrorResponse && error.status === 401) {
                    return this.handleUnauthorized(retryRequest, currentAttempt + 1);
                }
                return throwError(error);
            })
        );
    }

    /**
     * Esegue una richiesta HTTP con gestione automatica del 401
     */
    public executeWithAuth<T>(
        requestFactory: (token: string) => Observable<T>
    ): Observable<T> {
        return this.getValidToken().pipe(
            switchMap(token => requestFactory(token)),
            catchError(error => {
                if (error instanceof HttpErrorResponse && error.status === 401) {
                    return this.handleUnauthorized(requestFactory);
                }
                return throwError(error);
            })
        );
    }

    /**
     * Ottiene il dominio corrente del browser (hostname:port)
     */
    private getCurrentResource(): string {
        return window.location.host;
    }

    /**
     * Richiede un nuovo token OAuth tramite il microservizio Fea.Microservice
     */
    private requestOAuthToken(): Observable<OAuthTokenResponse> {
        const microserviceConfig = this._services.feaMicroservice;

        if (!microserviceConfig) {
            throw new Error('feaMicroservice configuration not found in services config');
        }

        const resource = this.getCurrentResource();

        const headers = new HttpHeaders({
            'X-Resource': resource,
            'X-API-Key': microserviceConfig.apiKey
        });

        const url = `${microserviceConfig.baseUrl}api/token`;

        return this.http.get<ApiResponse<MicroserviceTokenResponse>>(url, { headers }).pipe(
            map(response => {
                if (response.status !== 'Success' || !response.data?.access_token) {
                    throw new Error(response.message || 'Invalid microservice response: missing access_token');
                }
                return {
                    access_token: response.data.access_token,
                    expires_in: response.data.expires_in || 3600, // Default 1 ora se non fornito
                    token_type: response.data.token_type || 'Bearer'
                };
            })
        );
    }

    /**
     * Gestisce il retry con backoff esponenziale e rate limiting
     */
    private handleRetryWithBackoff(errors: Observable<any>, maxRetries: number): Observable<any> {
        let retryCount = 0;

        return errors.pipe(
            concatMap((error: HttpErrorResponse) => {
                retryCount++;

                if (retryCount > maxRetries) {
                    return throwError(error);
                }

                // Gestione specifica per rate limiting (429)
                if (error.status === 429) {
                    const retryAfter = error.headers?.get('Retry-After');
                    if (retryAfter) {
                        const delayMs = parseInt(retryAfter, 10) * 1000;
                        console.log(`Rate limited. Waiting ${delayMs}ms before retry ${retryCount}/${maxRetries}`);
                        return of(error).pipe(delay(delayMs));
                    }
                }

                // Calcola delay con backoff esponenziale per altri errori
                const delayMs = Math.min(
                    this.config.initialDelayMs * Math.pow(this.config.backoffMultiplier, retryCount - 1),
                    this.config.maxDelayMs
                );

                console.log(`Retry attempt ${retryCount}/${maxRetries} after ${delayMs}ms`);

                return of(error).pipe(delay(delayMs));
            })
        );
    }

    /**
     * Attende che il refresh del token sia completato
     */
    private waitForTokenRefresh(): Observable<string> {
        return this._refreshTokenSubject.pipe(
            switchMap(token => {
                if (token === null) {
                    // Ancora in attesa
                    return of(null as any);
                }
                return of(token);
            }),
            // Filtra i null e prendi il primo valore valido
            concatMap(token => token ? of(token) : throwError(new Error('Waiting for token'))),
            take(1),
            catchError(() => {
                // Se il refresh fallisce, attendi e riprova
                return this._refreshTokenSubject.pipe(
                    delay(100),
                    switchMap(token => token ? of(token) : throwError(new Error('Token refresh failed')))
                );
            })
        );
    }

    /**
     * Verifica se il token corrente è valido
     */
    private isTokenValid(): boolean {
        if (!this._accessToken) {
            return false;
        }

        // Se abbiamo una data di scadenza, verifica che non sia scaduto
        if (this._tokenExpiry) {
            const now = new Date();
            // Considera il token scaduto 30 secondi prima per sicurezza
            const bufferMs = 30000;
            return this._tokenExpiry.getTime() > (now.getTime() + bufferMs);
        }

        // Se non abbiamo scadenza, considera il token valido
        return true;
    }

    /**
     * Memorizza il token e la sua scadenza
     */
    private storeToken(token: string, expiresIn?: number): void {
        this._accessToken = token;

        if (expiresIn) {
            this._tokenExpiry = new Date(Date.now() + (expiresIn * 1000));
        } else {
            // Default: 1 ora
            this._tokenExpiry = new Date(Date.now() + (3600 * 1000));
        }
    }

    /**
     * Cancella il token corrente
     */
    public clearToken(): void {
        this._accessToken = null;
        this._tokenExpiry = null;
    }

    /**
     * Ottiene il token corrente (può essere null)
     */
    public getCurrentToken(): string | null {
        return this._accessToken;
    }

    // ==================== Circuit Breaker ====================

    /**
     * Verifica se è possibile effettuare una richiesta (circuit breaker)
     */
    private canMakeRequest(): boolean {
        switch (this._circuitState) {
            case CircuitState.CLOSED:
                return true;

            case CircuitState.OPEN:
                // Verifica se è passato abbastanza tempo per provare a chiudere
                if (this._circuitOpenedAt) {
                    const elapsed = Date.now() - this._circuitOpenedAt.getTime();
                    if (elapsed >= this.config.circuitBreakerTimeoutMs) {
                        this._circuitState = CircuitState.HALF_OPEN;
                        return true;
                    }
                }
                return false;

            case CircuitState.HALF_OPEN:
                return true;
        }
    }

    /**
     * Registra un fallimento per il circuit breaker
     */
    private recordFailure(): void {
        this._failureCount++;
        this._lastFailureTime = new Date();

        if (this._failureCount >= this.config.circuitBreakerThreshold) {
            this._circuitState = CircuitState.OPEN;
            this._circuitOpenedAt = new Date();
            console.warn('Circuit breaker OPENED due to multiple failures');
        }
    }

    /**
     * Resetta il circuit breaker dopo un successo
     */
    private resetCircuitBreaker(): void {
        this._failureCount = 0;
        this._circuitState = CircuitState.CLOSED;
        this._circuitOpenedAt = null;
    }

    /**
     * Ottiene lo stato corrente del circuit breaker
     */
    public getCircuitState(): CircuitState {
        return this._circuitState;
    }

    /**
     * Forza la chiusura del circuit breaker (per testing/admin)
     */
    public resetCircuit(): void {
        this.resetCircuitBreaker();
    }

    // ==================== Error Handling ====================

    /**
     * Crea un oggetto errore standardizzato per l'UI
     */
    private createAuthError(
        code: string,
        message: string,
        userFriendlyMessage: string,
        canRetry: boolean
    ): AuthErrorMessage {
        return {
            code,
            message,
            userFriendlyMessage,
            canRetry
        };
    }

    /**
     * Ottiene i messaggi di errore localizzati
     */
    public getErrorMessages(): { [key: string]: string } {
        return {
            'CIRCUIT_OPEN': 'Il servizio di autenticazione è temporaneamente non disponibile. Riprova tra qualche minuto.',
            'TOKEN_ACQUISITION_FAILED': 'Impossibile ottenere l\'autorizzazione. Verifica la connessione e riprova.',
            'MAX_REFRESH_RETRIES_EXCEEDED': 'Sessione scaduta. Effettua nuovamente l\'accesso.',
            'NETWORK_ERROR': 'Errore di rete. Verifica la connessione internet.',
            'UNKNOWN_ERROR': 'Si è verificato un errore imprevisto. Riprova.'
        };
    }
}
